import { useState } from 'react';
import PropTypes from 'prop-types';
import { Transition } from '@headlessui/react';
import { BillNoteIcon } from 'icons/solid';
import SwitchButton from '../../atoms/SwitchButton';
import FormInvoiceInstructions from '../../molecules/FormInvoiceInstructions';
import FormInvoiceTicket from '../../molecules/FormInvoiceTicket';
import FormFindOrderInvoice from '../../molecules/FormFindOrderInvoice/FormFindOrderInvoice';
import Alert from '../../molecules/Alert';
import Section from '../../layouts/Section';

const getErrorMessage = (err) => {
    let errorMessage;
    switch (err) {
        case 'Esta factura ya fue solicitada':
            errorMessage =
                'Lo sentimos, sólo se puede generar una factura por pedido. Échale un ojo a tu bandeja de entrada en tu correo, casi seguro que ya está por allá 🙏.';
            break;

        case 'Error para generación de factura':
            errorMessage =
                'Lo sentimos, solo tienes 30 días naturales para solicitar tu factura 😔';
            break;

        case 'Ticket no localizado':
            errorMessage =
                '¡Ups! Algo salió mal, checa los datos de tu ticket y vuelve a ingresarlos 😬 ';

            break;

        case 'Pedido no localizado':
            errorMessage =
                '¡Ups! Algo salió mal, checa los datos de tu pedido y vuelve a ingresarlos 😬 ';
            break;

        default:
            return;
    }

    return errorMessage;
};

const PurchaseOffline = ({
    className,
    ticketValues,
    ticketStatus,
    setTicketValues,
    isLoading,
    isDesktop,
    handleConsultTicket,
    handleResetValues,
}) => {
    const { ticketId, ticketFound, ticketTotalSale, message, canRequest, isInitialState } =
        ticketStatus;
    const foundAndCanRequest = ticketFound && canRequest;
    const errorMessage = getErrorMessage(message);

    return (
        <div className={className}>
            <FormInvoiceInstructions context='offline' isDesktop={isDesktop} />

            <FormInvoiceTicket
                isLoading={isLoading}
                isDesktop={isDesktop}
                ticketValues={ticketValues}
                ticketStatus={ticketStatus}
                setTicketValues={setTicketValues}
                handleConsultTicket={handleConsultTicket}
                handleResetValues={handleResetValues}
            >
                {!isLoading && ticketStatus && !isInitialState && (
                    <Alert
                        icon={
                            foundAndCanRequest && (
                                <BillNoteIcon className='mr-2 flex w-6 self-start' />
                            )
                        }
                        className='order-2 py-1 md:order-last'
                        variant={foundAndCanRequest ? 'informative' : 'error'}
                        title={message}
                    >
                        {foundAndCanRequest ? (
                            <p className='my-0 ml-8 grid grid-cols-2 text-start'>
                                <span className='col-span-2 lg:col-span-1'>
                                    <strong>Num Ticket: </strong>

                                    {ticketId}
                                </span>
                                <span className='col-span-2 lg:col-span-1'>
                                    <strong>Total:</strong> ${ticketTotalSale}
                                </span>
                            </p>
                        ) : (
                            <span className='ml-8 flex text-start'>{errorMessage}</span>
                        )}
                    </Alert>
                )}
            </FormInvoiceTicket>
        </div>
    );
};

const PurchaseOnline = ({
    className,
    isLoading,
    orderStatus,
    orderValues,
    setOrderValues,
    isDesktop,
    handleConsultOrder,
    handleResetValues,
}) => {
    const { found, canRequest, message, date, total, isInitialState } = orderStatus;
    const foundAndCanRequest = found && canRequest;
    const errorMessage = getErrorMessage(message);

    return (
        <div className={className}>
            <FormInvoiceInstructions context='online' isDesktop={isDesktop} />

            <FormFindOrderInvoice
                isLoading={isLoading}
                isDesktop={isDesktop}
                orderValues={orderValues}
                orderResult={orderStatus}
                setOrderValues={setOrderValues}
                handleConsultOrder={handleConsultOrder}
                handleResetValues={handleResetValues}
            >
                {!isLoading && orderStatus && !isInitialState && (
                    <Alert
                        icon={
                            foundAndCanRequest && (
                                <BillNoteIcon className='mr-2 flex w-6 self-start' />
                            )
                        }
                        className='order-first py-1 md:order-last'
                        variant={foundAndCanRequest ? 'informative' : 'error'}
                        title={message}
                    >
                        {foundAndCanRequest ? (
                            <p className='my-0 ml-8 grid grid-cols-2 text-start'>
                                <span className='col-span-2 lg:col-span-1'>
                                    <strong>Fecha de compra: </strong>

                                    {date}
                                </span>
                                <span className='col-span-2 lg:col-span-1'>
                                    <strong>Total:</strong> ${total}
                                </span>
                            </p>
                        ) : (
                            <span className='ml-8 flex text-start'>{errorMessage}</span>
                        )}
                    </Alert>
                )}
            </FormFindOrderInvoice>
        </div>
    );
};

const InvoiceRequest = ({
    isDesktop,
    isLoading,
    ticketStatus,
    orderStatus,
    orderValues,
    ticketValues,
    setOrderValues,
    setTicketValues,
    handleConsultTicket,
    handleConsultOrder,
    handleResetValues,
}) => {
    const [isOnline, setIsOnline] = useState(false);

    return (
        <Section className='bg-brand-tertiary-100 text-center'>
            <div className='container max-w-3xl'>
                <h2 className='lg:mb-12'>Facturación Yema 📄</h2>

                <h3 className='lg:mb-4'>
                    ¡Hola! gracias por comprar en Yema, <br />
                    ¿necesitas una factura?
                </h3>

                <p className='lg:mb-15'>Selecciona tu tipo de compra:</p>

                <SwitchButton
                    onClick={() => setIsOnline(!isOnline)}
                    enabled={isOnline}
                    labelOn='Compra en tienda'
                    labelOff='Compra online'
                />

                <Transition
                    className='inline-block w-full'
                    enter='transform transition ease-in-out duration-300'
                    enterFrom='-translate-x-full opacity-0'
                    enterTo='translate-x-0 opacity-100'
                    leave='transform transition ease-in-out duration-300'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0 absolute top-4 hidden'
                    show={isOnline}
                >
                    <PurchaseOnline
                        className='mt-8 lg:mt-12'
                        isDesktop={isDesktop}
                        isLoading={isLoading}
                        orderStatus={orderStatus}
                        orderValues={orderValues}
                        setOrderValues={setOrderValues}
                        handleConsultOrder={handleConsultOrder}
                        handleResetValues={handleResetValues}
                    />
                </Transition>

                <Transition
                    className='inline-block w-full'
                    enter='transform transition ease-in-out duration-300'
                    enterFrom='-translate-x-full opacity-0'
                    enterTo='translate-x-0 opacity-100'
                    leave='transform transition ease-in-out duration-300'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0 absolute top-4 hidden'
                    show={!isOnline}
                >
                    <PurchaseOffline
                        className='mt-8 lg:mt-12'
                        ticketValues={ticketValues}
                        ticketStatus={ticketStatus}
                        setTicketValues={setTicketValues}
                        isLoading={isLoading}
                        isDesktop={isDesktop}
                        handleConsultTicket={handleConsultTicket}
                        handleResetValues={handleResetValues}
                    />
                </Transition>
            </div>
        </Section>
    );
};

InvoiceRequest.propTypes = {
    isDesktop: PropTypes.bool,
    isLoading: PropTypes.bool,
    handleConsultTicket: PropTypes.func,
    handleConsultOrder: PropTypes.func,
    ticketStatus: PropTypes.object,
    orderStatus: PropTypes.object,
    ticketValues: PropTypes.object,
    orderValues: PropTypes.object,
    setTicketValues: PropTypes.func,
    setOrderValues: PropTypes.func,
    handleResetValues: PropTypes.func,
};

export default InvoiceRequest;
